import { z } from 'zod';
import { queryBoolean } from './query.js';
import { SportNameSchema, SPORT_NAME_VALUES } from './profile.js';
import type { SportName } from './profile.js';
import type { SubVenue } from './sub-venue.js';
import type { VenuePricingBand } from './venue-pricing.js';
import type { VenueAmenityMaster } from './venue-amenity.js';
import type { VenueClosure } from './venue-closure.js';

/**
 * F10 venue-catalog types + Zod schemas.
 *
 * Decisions: see plans/10_F10_venue_catalog.md (Decisions 1–22).
 *
 * ─── Status lifecycle ───────────────────────────────────────────────────────
 *   draft → active → suspended → closed → merged
 *
 * ─── Tier + claim-state ─────────────────────────────────────────────────────
 *   tier_1 (default, Tier 1 listing) | tier_2 | tier_3
 *   unclaimed (default) | claim_pending | claimed
 *   Both columns reserved for F11 claim flow — F10 seeds defaults only.
 *
 * ─── Moderation queues ──────────────────────────────────────────────────────
 *   VenueSubmission: new venues from public users (pending → approved | rejected | merged)
 *   VenueEdit: field-level diffs suggested by users (pending → applied | rejected)
 */

// ─── Enum constants ──────────────────────────────────────────────────────────

export const VENUE_STATUSES = ['draft', 'active', 'suspended', 'closed', 'merged'] as const;
export type VenueStatus = (typeof VENUE_STATUSES)[number];

export const VENUE_TIERS = ['tier_1', 'tier_2', 'tier_3'] as const;
export type VenueTier = (typeof VENUE_TIERS)[number];

export const VENUE_CLAIM_STATUSES = ['unclaimed', 'claim_pending', 'claimed'] as const;
export type VenueClaimStatus = (typeof VENUE_CLAIM_STATUSES)[number];

export const VENUE_CREATED_BY_KINDS = ['admin', 'user_submission', 'b2b_claim'] as const;
export type VenueCreatedByKind = (typeof VENUE_CREATED_BY_KINDS)[number];

export const VENUE_PHOTO_MODERATION_STATUSES = ['approved', 'pending', 'rejected'] as const;
export type VenuePhotoModerationStatus = (typeof VENUE_PHOTO_MODERATION_STATUSES)[number];

export const VENUE_SUBMISSION_STATUSES = ['pending', 'approved', 'rejected', 'merged'] as const;
export type VenueSubmissionStatus = (typeof VENUE_SUBMISSION_STATUSES)[number];

export const VENUE_EDIT_STATUSES = ['pending', 'applied', 'rejected'] as const;
export type VenueEditStatus = (typeof VENUE_EDIT_STATUSES)[number];

export const VENUE_PHOTO_MIMES = ['image/jpeg', 'image/png', 'image/heic', 'image/webp'] as const;
export type VenuePhotoMime = (typeof VENUE_PHOTO_MIMES)[number];

export const VENUE_PHOTO_MAX_BYTES = 5 * 1024 * 1024; // 5 MB
export const VENUE_PHOTO_MAX_COUNT = 10;
export const VENUE_SLUG_REGEX = /^[a-z0-9-]{1,80}$/;

// ─── Structural types ────────────────────────────────────────────────────────

/**
 * A single opening interval within a day. `open` and `close` are HH:mm strings
 * (24-hour clock, e.g. "08:00", "23:30"). A venue may have multiple intervals
 * per day (e.g. morning + evening sessions).
 */
export type VenueOpeningInterval = {
  readonly open: string; // HH:mm
  readonly close: string; // HH:mm
};

/**
 * Opening hours for the full week. Each day is an array of intervals:
 *   - `null` means "no information available"
 *   - `[]` means "closed that day"
 *   - one or more intervals means "open during those windows"
 */
export type VenueOpeningHours = {
  readonly mon: ReadonlyArray<VenueOpeningInterval>;
  readonly tue: ReadonlyArray<VenueOpeningInterval>;
  readonly wed: ReadonlyArray<VenueOpeningInterval>;
  readonly thu: ReadonlyArray<VenueOpeningInterval>;
  readonly fri: ReadonlyArray<VenueOpeningInterval>;
  readonly sat: ReadonlyArray<VenueOpeningInterval>;
  readonly sun: ReadonlyArray<VenueOpeningInterval>;
};

/**
 * Optional social / web links on a venue. All fields are optional.
 * URLs are stored as strings (validated at the API layer).
 */
export type VenueSocialLinks = {
  readonly instagram?: string;
  readonly facebook?: string;
  readonly tiktok?: string;
  readonly website?: string;
};

// ─── Raw DB row shapes ───────────────────────────────────────────────────────

export type Venue = {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly status: VenueStatus;
  readonly tier: VenueTier;
  readonly claimStatus: VenueClaimStatus;
  readonly ownerB2bAccountId: string | null;
  readonly mergedIntoVenueId: string | null;
  readonly countryCode: string;
  readonly governorateIso: string;
  readonly cazaCode: string;
  readonly cityCode: string;
  readonly streetAddress: string | null;
  readonly lat: string; // numeric driver returns string
  readonly lng: string; // numeric driver returns string
  readonly phone: string;
  readonly whatsappPhone: string | null;
  readonly openingHours: VenueOpeningHours | null;
  readonly socialLinks: VenueSocialLinks | null;
  readonly description: string | null;
  readonly coverPhotoId: string | null;
  readonly taxMode: string;
  readonly taxRatePercent: string; // numeric driver returns string
  readonly createdByKind: VenueCreatedByKind;
  readonly createdByUserId: string | null;
  readonly createdByAdminId: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  /**
   * F09 v2 (E32) — the venue tick (`venues.verification_status = 'verified'`),
   * populated on the public detail (`venue.verified`) and owner editor reads.
   */
  readonly verified: boolean;
  /** F09 v2 — display-only "since" date (`venues.verified_at`); null when unverified. */
  readonly verifiedAt: string | null;
};

/**
 * F09 v2 — public affiliated-trainer projection inside the public venue detail
 * (moved here from `apps/api/src/services/venue.ts`, Phase-2 seam — no UI at
 * MVP). `verified` is re-sourced to the TRAINER tick
 * (`trainer_profiles.verification_status`), `verifiedAt` to
 * `trainer_profiles.verified_at`.
 */
export interface PublicAffiliatedTrainer {
  readonly userId: string;
  readonly displayName: string;
  readonly avatarUrl: string | null;
  /** The trainer tick. */
  readonly verified: boolean;
  readonly verifiedAt: string | null;
  readonly headline: string | null;
  readonly yearsExperience: number | null;
  readonly sportsPreview: ReadonlyArray<{
    readonly sport: string;
    readonly effectiveLevel: number;
    readonly isPrimary: boolean;
  }>;
  readonly acceptingNewClients: boolean;
}

export type VenueSport = {
  readonly venueId: string;
  readonly sportName: SportName;
  readonly isPrimary: boolean;
  readonly subVenueCount: number | null;
  readonly createdAt: string;
};

/**
 * F12 Sharp pipeline output — three WebP variants (thumb / card / hero)
 * plus the archived original. URLs are absolute CloudFront paths (no signing,
 * no expiry — public-read). NULL for pre-F12 photo rows; consumers fall
 * back to the legacy `cdnUrl` in that case.
 *
 * Defined here (rather than co-located on `venue-profile.ts`) so it can be
 * referenced from `VenuePhoto` without a circular module import
 * (`venue-profile.ts` already imports `Venue`/`VenuePhoto` from this file).
 * `venue-profile.ts` re-exports the type so existing imports keep working.
 */
export type VenuePhotoVariants = {
  readonly thumbUrl: string; // 200×200 WebP (q75)
  readonly cardUrl: string; // 800×800 WebP (q80)
  readonly heroUrl: string; // 1600×1200 WebP (q85)
  readonly originalUrl: string; // archived original (jpeg/png/heic/webp)
};

export type VenuePhoto = {
  readonly id: string;
  readonly venueId: string;
  readonly s3Key: string;
  readonly cdnUrl: string;
  readonly altText: string | null;
  readonly sortOrder: number;
  readonly moderationStatus: VenuePhotoModerationStatus;
  readonly uploadedByUserId: string | null;
  readonly uploadedByAdminId: string | null;
  readonly widthPx: number | null;
  readonly heightPx: number | null;
  readonly sizeBytes: number | null;
  readonly mime: VenuePhotoMime | null;
  readonly sha256: string | null;
  /**
   * F12 Sharp pipeline output — three WebP variants (thumb / card / hero)
   * plus the archived original. NULL on pre-F12 photo rows; in that case
   * consumers fall back to the legacy `cdnUrl`.
   */
  readonly variants?: VenuePhotoVariants | null;
  readonly createdAt: string;
  readonly updatedAt: string;
};

export type VenueSubmission = {
  readonly id: string;
  readonly status: VenueSubmissionStatus;
  readonly payload: VenueSubmissionPayload;
  readonly potentialDuplicateOf: string | null;
  readonly submittedByUserId: string | null;
  readonly submitterIpHash: string;
  readonly submitterEmail: string | null;
  readonly reviewedByAdminId: string | null;
  readonly reviewedAt: string | null;
  readonly decisionReason: string | null;
  readonly resultingVenueId: string | null;
  readonly mergedIntoVenueId: string | null;
  readonly countryCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};

export type VenueEdit = {
  readonly id: string;
  readonly venueId: string;
  readonly status: VenueEditStatus;
  readonly fieldChanges: VenueEditDiff;
  readonly submittedByUserId: string | null;
  readonly submitterIpHash: string;
  readonly submitterEmail: string | null;
  readonly reviewedByAdminId: string | null;
  readonly reviewedAt: string | null;
  readonly decisionReason: string | null;
  readonly countryCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};

export type VenueAlias = {
  readonly oldSlug: string;
  readonly canonicalVenueId: string;
  readonly createdAt: string;
};

// ─── Payload / diff shapes (stored as jsonb) ─────────────────────────────────

/**
 * The JSON blob stored in `venue_submissions.payload`.
 * Required fields: name, phone, governorateIso, cazaCode, cityCode, lat, lng,
 *   sports (min 1).
 * Optional: whatsappPhone, streetAddress, openingHours, socialLinks,
 *   description, primarySport.
 */
export type VenueSubmissionPayload = {
  readonly name: string;
  readonly phone: string;
  readonly whatsappPhone?: string;
  readonly governorateIso: string;
  readonly cazaCode: string;
  readonly cityCode: string;
  readonly streetAddress?: string;
  readonly lat: number;
  readonly lng: number;
  readonly sports: ReadonlyArray<SportName>;
  readonly primarySport?: SportName;
  readonly openingHours?: VenueOpeningHours;
  readonly socialLinks?: VenueSocialLinks;
  readonly description?: string;
};

/**
 * The JSON blob stored in `venue_edits.field_changes`.
 * Maps each changed field key to `{ old: unknown; new: unknown }`.
 * Kept intentionally generic — Zod validation accepts any subset.
 */
export type VenueEditDiff = Partial<
  Record<keyof VenueSubmissionPayload, { old: unknown; new: unknown }>
>;

// ─── API response projections ────────────────────────────────────────────────

/**
 * Lightweight shape used in paginated list endpoints (`GET /public/venues`).
 * Only includes cover_photo_url and the sport/location fields needed by VenueCard.
 */
export type VenueListItem = {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly status: VenueStatus;
  readonly tier: VenueTier;
  readonly claimStatus: VenueClaimStatus;
  readonly countryCode: string;
  readonly governorateIso: string;
  readonly cazaCode: string;
  readonly cityCode: string;
  /**
   * Display names for the city / caza (English at MVP). Codes such as
   * `LB-JL-jbeil` are identifiers and must never be shown to users. Optional so
   * hand-built fixtures stay valid; `GET /public/venues` always sets them.
   */
  readonly cityName?: string | null;
  readonly cazaName?: string | null;
  readonly lat: string;
  readonly lng: string;
  readonly phone: string;
  readonly whatsappPhone: string | null;
  readonly description: string | null;
  readonly coverPhotoUrl: string | null;
  readonly sports: ReadonlyArray<{ sportName: SportName; isPrimary: boolean }>;
  /**
   * F09 v2 (E32) — the venue tick (`venues.verification_status = 'verified'`).
   * NOT derived from `claimStatus` (B17 — the old claimed ⇒ tick wiring is removed).
   */
  readonly verified: boolean;
  /**
   * Whether the venue accepts in-app booking requests (active, claimed,
   * tier_2/tier_3 and its bookable flag enabled). Independent of verification
   * and slot availability. The public list always sets this; optional for
   * compatibility with existing hand-built client fixtures.
   */
  readonly bookable?: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
};

/**
 * Full detail shape returned by `GET /public/venues/:slugOrId`.
 * Includes the full photo gallery + opening hours + social links.
 * Merged venues return a redirect shape; closed venues return a closed-banner shape.
 *
 * F12 (Section 3.4 — additive only) — extends the `active` shape with four
 * new top-level fields populated from the F12 child tables:
 *   - `subVenues`  — only `status='active'` rows, ordered by `ordinal`
 *   - `pricing`    — only bands attached to active sub_venues, ordered by
 *                    (sub_venue.ordinal, sport_name, day_of_week, start_minute)
 *   - `amenities`  — full join through `venue_amenities` →
 *                    `venue_amenities_master`, ordered by `(category, sort_order)`
 *   - `closures`   — only `end_date >= today` (future + currently active),
 *                    ordered by `start_date`
 *
 * Every existing F10/F11 consumer continues to read what it read before; the
 * new fields are additive. The 5-minute F10 cache busts on every F12
 * mutation via `VenueProfileService.invalidateVenueCaches(slug)`.
 *
 * The shape uses dynamic imports for the F12 types because of the cross-file
 * dependency direction (`venue-profile.ts` imports `venue.ts`); the explicit
 * `import('./sub-venue.js')` syntax avoids a circular type import while keeping
 * the public type surface narrow.
 */
export type VenueDetailResponse =
  | {
      readonly kind: 'active';
      readonly venue: Venue;
      /**
       * True when the venue takes in-app bookings — the same gate
       * `BookingService.create` enforces (`venues.bookable` AND claimed AND
       * tier_2/tier_3; the response is only `active` for active venues).
       * Clients show the "Book" CTA only when set. Optional so hand-built
       * fixtures stay valid; `GET /public/venues/:slugOrId` always sets it.
       */
      readonly bookable?: boolean;
      readonly sports: ReadonlyArray<VenueSport>;
      readonly photos: ReadonlyArray<VenuePhoto>;
      readonly subVenues: ReadonlyArray<SubVenue>;
      readonly pricing: ReadonlyArray<VenuePricingBand>;
      readonly amenities: ReadonlyArray<VenueAmenityMaster>;
      readonly closures: ReadonlyArray<VenueClosure>;
    }
  | {
      readonly kind: 'closed';
      readonly venue: Pick<
        Venue,
        'id' | 'slug' | 'name' | 'status' | 'countryCode' | 'createdAt' | 'updatedAt'
      >;
    }
  | {
      readonly kind: 'merged';
      readonly canonicalSlug: string;
    };

/**
 * Entry returned by `GET /public/venues/sitemap.json`.
 */
export type VenueSitemapEntry = {
  readonly slug: string;
  readonly lastModified: string;
};

// ─── Input / request schemas ─────────────────────────────────────────────────

const VenueOpeningIntervalSchema = z
  .object({
    open: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'must be HH:mm'),
    // A close before the open means the next morning (the 22:00-06:00 night band).
    close: z.string().regex(/^(([01]\d|2[0-3]):[0-5]\d|24:00)$/, 'must be HH:mm'),
  })
  .strict();

export const VenueOpeningHoursSchema = z
  .object({
    mon: z.array(VenueOpeningIntervalSchema),
    tue: z.array(VenueOpeningIntervalSchema),
    wed: z.array(VenueOpeningIntervalSchema),
    thu: z.array(VenueOpeningIntervalSchema),
    fri: z.array(VenueOpeningIntervalSchema),
    sat: z.array(VenueOpeningIntervalSchema),
    sun: z.array(VenueOpeningIntervalSchema),
  })
  .strict();

export const VenueSocialLinksSchema = z
  .object({
    instagram: z.string().url().optional(),
    facebook: z.string().url().optional(),
    tiktok: z.string().url().optional(),
    website: z.string().url().optional(),
  })
  .strict();

// ─── Enum schemas ─────────────────────────────────────────────────────────────

export const VenueStatusSchema = z.enum(VENUE_STATUSES);
export const VenueTierSchema = z.enum(VENUE_TIERS);
export const VenueClaimStatusSchema = z.enum(VENUE_CLAIM_STATUSES);
export const VenueCreatedByKindSchema = z.enum(VENUE_CREATED_BY_KINDS);
export const VenuePhotoModerationStatusSchema = z.enum(VENUE_PHOTO_MODERATION_STATUSES);
export const VenueSubmissionStatusSchema = z.enum(VENUE_SUBMISSION_STATUSES);
export const VenueEditStatusSchema = z.enum(VENUE_EDIT_STATUSES);
export const VenuePhotoMimeSchema = z.enum(VENUE_PHOTO_MIMES);

// ─── Submission payload + edit diff ──────────────────────────────────────────

const VenueSubmissionFieldsSchema = z
  .object({
    name: z.string().min(3).max(120),
    phone: z.string().min(1).max(20),
    whatsappPhone: z.string().min(1).max(20).optional(),
    governorateIso: z.string().min(1).max(20),
    cazaCode: z.string().min(1).max(50),
    cityCode: z.string().min(1).max(50),
    streetAddress: z.string().min(1).max(240).optional(),
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
    sports: z.array(SportNameSchema).min(1, 'at least one sport is required').max(41),
    primarySport: SportNameSchema.optional(),
    openingHours: VenueOpeningHoursSchema.optional(),
    socialLinks: VenueSocialLinksSchema.optional(),
    description: z.string().max(500).optional(),
  })
  .strict();

type VenueSubmissionFields = z.infer<typeof VenueSubmissionFieldsSchema>;

const refineVenueSubmission = (val: VenueSubmissionFields, ctx: z.RefinementCtx) => {
  if (
    val.primarySport !== undefined &&
    !(val.sports as readonly string[]).includes(val.primarySport)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'primarySport must be one of the sports listed in sports[]',
      path: ['primarySport'],
    });
  }
};

export const VenueSubmissionPayloadSchema =
  VenueSubmissionFieldsSchema.superRefine(refineVenueSubmission);

/**
 * Edit diff schema — accepts any subset of `VenueSubmissionPayload` keys,
 * each mapped to `{ old, new }`. Zod validates it is an object with known
 * keys; the values are intentionally `unknown` (old + new may be any valid
 * JSON).
 */
/**
 * Each changed field maps to `{ old, new }` where both keys MUST be present
 * (value may be any JSON). We use `z.union([z.null(), z.undefined(), z.boolean(),
 * z.number(), z.string(), z.array(z.unknown()), z.record(z.unknown())])` to
 * accept any JSON value while still requiring the key is explicitly present.
 *
 * Note: `z.unknown()` in Zod 3 does NOT enforce key presence — a missing key
 * is treated as acceptable. We use a custom required wrapper instead.
 */
const _anyJsonValue = z.union([
  z.null(),
  z.boolean(),
  z.number(),
  z.string(),
  z.array(z.unknown()),
  z.record(z.string(), z.unknown()),
]);

export const VenueEditDiffSchema = z.record(
  z.string().min(1),
  z.object({ old: _anyJsonValue, new: _anyJsonValue }).strict(),
);

// ─── Create / update input schemas ───────────────────────────────────────────

/**
 * Admin and user-submission create input.
 * Requires: name, phone, governorateIso, cazaCode, cityCode, lat, lng, sports.
 * Optional everything else.
 */
export const CreateVenueInputSchema = VenueSubmissionPayloadSchema;
export type CreateVenueInput = z.infer<typeof CreateVenueInputSchema>;

/**
 * Admin partial update — all fields optional. Unknown keys rejected.
 */
export const UpdateVenueInputSchema = z
  .object({
    name: z.string().min(3).max(120).optional(),
    phone: z.string().min(1).max(20).optional(),
    whatsappPhone: z.string().min(1).max(20).nullable().optional(),
    streetAddress: z.string().min(1).max(240).nullable().optional(),
    openingHours: VenueOpeningHoursSchema.nullable().optional(),
    socialLinks: VenueSocialLinksSchema.nullable().optional(),
    description: z.string().max(500).nullable().optional(),
  })
  .strict();
export type UpdateVenueInput = z.infer<typeof UpdateVenueInputSchema>;

// ─── Photo input schemas ──────────────────────────────────────────────────────

export const CreateVenuePhotoInputSchema = z
  .object({
    s3Key: z.string().min(1).max(512),
    cdnUrl: z.string().url().max(1024),
    altText: z.string().max(255).optional(),
    sortOrder: z.number().int().min(0).optional(),
    mime: VenuePhotoMimeSchema,
    sizeBytes: z.number().int().min(1).max(VENUE_PHOTO_MAX_BYTES),
    sha256: z.string().length(64).optional(),
    widthPx: z.number().int().positive().optional(),
    heightPx: z.number().int().positive().optional(),
  })
  .strict();
export type CreateVenuePhotoInput = z.infer<typeof CreateVenuePhotoInputSchema>;

// ─── Submission input schemas ─────────────────────────────────────────────────

/** What a submit form sends: the venue, plus an optional way for a guest to hear back. */
export const CreateVenueSubmissionInputSchema = VenueSubmissionFieldsSchema.extend({
  submitterEmail: z.string().email().max(254).optional(),
}).superRefine(refineVenueSubmission);
export type CreateVenueSubmissionInput = z.infer<typeof CreateVenueSubmissionInputSchema>;

export const ApproveVenueSubmissionInputSchema = z
  .object({
    overrides: UpdateVenueInputSchema.optional(),
  })
  .strict();
export type ApproveVenueSubmissionInput = z.infer<typeof ApproveVenueSubmissionInputSchema>;

export const MergeVenueSubmissionInputSchema = z
  .object({
    mergeIntoVenueId: z.string().uuid(),
    reason: z.string().min(1).max(500),
  })
  .strict();
export type MergeVenueSubmissionInput = z.infer<typeof MergeVenueSubmissionInputSchema>;

export const RejectVenueSubmissionInputSchema = z
  .object({
    reason: z.string().min(1).max(500),
  })
  .strict();
export type RejectVenueSubmissionInput = z.infer<typeof RejectVenueSubmissionInputSchema>;

// ─── Edit input schemas ───────────────────────────────────────────────────────

export const CreateVenueEditInputSchema = z
  .object({
    venueId: z.string().uuid(),
    fieldChanges: VenueEditDiffSchema,
    submitterEmail: z.string().email().optional(),
  })
  .strict();
export type CreateVenueEditInput = z.infer<typeof CreateVenueEditInputSchema>;

export const ApplyVenueEditInputSchema = z
  .object({
    reason: z.string().min(1).max(500).optional(),
  })
  .strict();
export type ApplyVenueEditInput = z.infer<typeof ApplyVenueEditInputSchema>;

export const RejectVenueEditInputSchema = z
  .object({
    reason: z.string().min(1).max(500),
  })
  .strict();
export type RejectVenueEditInput = z.infer<typeof RejectVenueEditInputSchema>;

// ─── Slug validation helper ───────────────────────────────────────────────────

export const VenueSlugSchema = z
  .string()
  .regex(VENUE_SLUG_REGEX, 'slug must be 1-80 lowercase alphanumeric or hyphen characters');

// ─── F14 — Public venue discovery: sort + filter surface ─────────────────────

/**
 * Sort order accepted by `GET /public/venues?sort=...`.
 *
 * - `recommended` — default; at F14 = pass-through (DB order:
 *   `tier_3 > tier_2 > tier_1` then `created_at DESC`). F39 retrofits a
 *   `RankingService` against this enum value without an API contract change.
 * - `nearest` — requires `lat` + `lng` query params; service responds with
 *   `422 SORT_REQUIRES_GEO` otherwise. Ordered by Haversine distance ASC.
 * - `name` — alphabetical by `venues.name` ASC, case-insensitive.
 * - `newest` — by `venues.created_at` DESC.
 */
export const VENUE_SORTS = ['recommended', 'nearest', 'name', 'newest'] as const;
export type VenueSort = (typeof VENUE_SORTS)[number];
export const VenueSortSchema = z.enum(VENUE_SORTS);

/**
 * Combined filter schema for `GET /public/venues`.
 *
 * F14 extends F10's filter set with `openNow`, `tier`, `featured`, `sort`,
 * `priceMin`, `priceMax`. The `tier` query param is a comma-separated list
 * of `tier_1 | tier_2 | tier_3` values (e.g. `tier=tier_2,tier_3`).
 * `sport` is a validated comma-separated OR selection, canonicalized by
 * trimming, deduplicating and sorting. `bookableOnly` and `verifiedOnly` are
 * independent restrictions; false leaves that dimension unrestricted.
 *
 * `featured` is accepted but no-op at F14 (reserved for F39 paid placements).
 * `priceMin` / `priceMax` are accepted but ignored at the service level until
 * F12 `venue_pricing` rows are queryable from the public endpoint.
 *
 * Use `z.input<...>` for the request shape (`tier` as a CSV string) and
 * `z.output<...>` for the parsed shape (`tier` as a string[]).
 */
const VenueSportFilterSchema = z
  .string()
  .max(2048)
  .transform((value) => value.split(',').map((sport) => sport.trim()))
  .pipe(z.array(SportNameSchema).min(1).max(SPORT_NAME_VALUES.length))
  .transform((sports) => [...new Set(sports)].sort().join(','));

export const VenueListFiltersSchema = z.object({
  // One sport remains valid; CSV means any matching sport (OR). Canonical
  // ordering gives equivalent selections the same service-cache key.
  sport: VenueSportFilterSchema.optional(),
  governorateIso: z.string().optional(),
  cazaCode: z.string().optional(),
  cityCode: z.string().optional(),
  q: z.string().optional(),
  lat: z.coerce.number().optional(),
  lng: z.coerce.number().optional(),
  radiusKm: z.coerce.number().min(1).max(100).optional(),
  openNow: queryBoolean().optional(),
  bookableOnly: queryBoolean().optional(),
  verifiedOnly: queryBoolean().optional(),
  tier: z
    .string()
    .optional()
    .transform((s) =>
      s
        ? s
            .split(',')
            .filter((t): t is 'tier_1' | 'tier_2' | 'tier_3' =>
              ['tier_1', 'tier_2', 'tier_3'].includes(t),
            )
        : undefined,
    ),
  featured: queryBoolean().optional(),
  sort: VenueSortSchema.optional().default('recommended'),
  priceMin: z.coerce.number().min(0).optional(),
  priceMax: z.coerce.number().min(0).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});

/**
 * Input shape — what a request sends. `tier` is a CSV string here.
 */
export type VenueListFilters = z.input<typeof VenueListFiltersSchema>;

/**
 * Parsed shape — what code sees after Zod parses. `tier` is a string[] of
 * allowed VenueTier values, or `undefined` if no valid tiers were supplied.
 */
export type VenueListFiltersParsed = z.output<typeof VenueListFiltersSchema>;

/**
 * `meta` block returned alongside the paginated list. Tells the client what
 * sort the API actually applied (in case of degrade — though F14 never
 * silent-degrades; `sort=nearest` without geo returns 422 instead).
 */
export type VenueListMeta = {
  readonly appliedSort: VenueSort;
  readonly totalReturned: number;
  readonly nextCursor: string | null;
};

// ─── Re-export SportName for consumers who only import from venue.ts ──────────

export type { SportName };
export { SPORT_NAME_VALUES };
